/**
 * "Ask Issa about this page" — the prompt behind the page-help button in the
 * context bar (components/ui/pageHelpButton.tsx).
 *
 * The answer comes from InstantAI's page documentation tool, which matches on the
 * page's internal name (`Web_Admin_Page.Name`, e.g. "viewplanograms"), so the prompt
 * always carries that exact name alongside the human title and menu path. No model is
 * forced: the quick chat picks the one it uses for any new conversation — Analyst
 * (`default`) where the endpoint qualifies, otherwise Guide (`assist`) — and both carry
 * the page-documentation workflow in their system prompts.
 */

/** Pages where explaining "this page" makes no sense — Issa's own surfaces. */
const EXCLUDED_PAGES = new Set(["issainsights", "agenticprompts"]);

export interface PageHelpInput {
  /** Internal page name from the route (`resolvePageName`). */
  pageName: string;
  /** `DisplayName` from the page cache; falls back to the internal name. */
  displayName?: string | null;
  /** Breadcrumb trail, outermost first, when the page sits in the menu. */
  trail?: readonly string[];
}

export interface PageHelpPrompt {
  prompt: string;
  displayPrompt: string;
}

export function isPageHelpAvailable(pageName: string | null | undefined): boolean {
  const name = (pageName ?? "").trim().toLowerCase();
  return name !== "" && !EXCLUDED_PAGES.has(name);
}

export function buildPageHelpPrompt({ pageName, displayName, trail }: PageHelpInput): PageHelpPrompt {
  const internal = pageName.trim();
  const title = (displayName ?? "").trim() || internal;
  const path = (trail ?? []).map((t) => t.trim()).filter(Boolean);
  const menuPath = path.length > 1 ? `; menu path: ${path.join(" → ")}` : "";

  const prompt = [
    `Explain the InstantCockpit page "${title}" (internal page name: ${internal}${menuPath}).`,
    "",
    "Look the page up by its exact internal name first. Then, briefly:",
    "1. What the page is for and who typically uses it.",
    "2. The columns or fields and the filter parameters that matter most, grouped by purpose. Name every one when there are up to about 15; otherwise cover the ones an operator needs and say how many more exist.",
    "3. What I can do on this page (actions, buttons, edits) and where those changes take effect.",
    "4. Anything that commonly confuses users about it.",
    "",
    "If there is no documentation for this exact page name, say so plainly and point me to the closest documented page or the support site instead of guessing.",
  ].join("\n");

  return {
    prompt,
    displayPrompt: `How does the "${title}" page work?`,
  };
}
